import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/common/dialog";
import { Button } from "@components/common/button";
import { PlusCircle } from "lucide-react";
import axios from "axios";
import {
  CONTRACTOR_COMPANY_ADD_ENDPOINT,
  CONTRACTOR_COMPANY_ALL_ENDPOINT,
  FINANCIAL_SERVER_URL,
  PRICE_LIST_ADD_ENDPOINT,
} from "@pma/utils/api-routes";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DictType } from "@pma/types";
import { toast } from "@pma/hooks/use-toast";
import useTranslator from "@pma/hooks/use-translator";
import { useAuth } from "@pma/stores/auth-store";
import { useState } from "react";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { parseCookies } from "nookies";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/common/select";
import { cn } from "@pma/utils/helpers";
import { Label } from "@components/common/label";
import { PriceListExcelMapper } from "./excel-mapper";
import { Input } from "@components/common/input";
import { Separator } from "@components/common/separator";

interface Props {
  revalidate: CallableFunction;
  companyId?: string;
}

const AddPriceList = ({ revalidate, companyId }: Props) => {
  const [fileInformation, setFileInformation] = useState<DictType>({
    Name: "",
    Number: "",
    Unit: "",
    Price: "",
    Description: "",
    File: "",
  });
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [stateCompanyId, setStateCompanyId] = useState(companyId);
  const [companyName, setCompanyName] = useState("");

  const { translate, locale } = useTranslator();
  const { authConfig } = useAuth();

  const orgId = authConfig?.last_organization_id;
  const accessToken = parseCookies()[ACCESS_TOKEN];

  const isHebrew = locale === "he";

  const getWorkspaceContractors = async () => {
    const response = await axios.get(
      FINANCIAL_SERVER_URL + CONTRACTOR_COMPANY_ALL_ENDPOINT,

      {
        params: {
          organization_id: orgId,
        },
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  };

  const { data: workspaceContractors, refetch } = useQuery({
    queryKey: ["workspace-contractors", orgId],
    queryFn: getWorkspaceContractors,
    enabled: !companyId && showUploadDialog,
  });

  const addPriceList = async () => {
    const excelFile = fileInformation?.File;
    const response = await axios.post(
      FINANCIAL_SERVER_URL + PRICE_LIST_ADD_ENDPOINT,
      {
        organization_id: orgId,
        colunm_name_number: fileInformation.Number || undefined,
        colum_name_unit: fileInformation?.Unit || undefined,
        colum_name_price: fileInformation?.Price || undefined,
        column_name_description: fileInformation?.Description || undefined,
        price_list_name: fileInformation?.Name || undefined,
        company_id: stateCompanyId,
        xsl_file: excelFile[0],
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response.data;
  };

  const addContractor = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + CONTRACTOR_COMPANY_ADD_ENDPOINT,
      {
        name: companyName,
        organization_id: orgId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const addPriceListMutation = useMutation({
    mutationKey: ["contractor"],
    mutationFn: addPriceList,
    onSuccess: (data) => {
      toast({
        title: translate("Success"),
        description: translate("Price list added successfully"),
        variant: "success",
      });

      setShowUploadDialog(false);
      setFileInformation({});

      revalidate && revalidate();
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      toast({
        title: translate("Error"),
        description: errorMessage || translate("An error occurred"),
        variant: "error",
      });
    },
  });
  const addContractorMutation = useMutation({
    mutationKey: ["contractor"],
    mutationFn: addContractor,
    onSuccess: (data) => {
      toast({
        title: translate("Success"),
        description: translate("Contractor added successfully"),
        variant: "success",
      });

      refetch();
      setCompanyName("");
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      toast({
        title: translate("Error"),
        description: errorMessage || translate("An error occurred"),
        variant: "error",
      });
    },
  });

  const onSubmit = () => {
    addPriceListMutation.mutate();
  };

  const onSubmitContractor = (e: any) => {
    e.preventDefault();
    if (companyName) {
      addContractorMutation.mutate();
    }
  };

  return (
    <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
      <DialogTrigger>
        <Button size={"sm"}>
          <PlusCircle className={cn('h-4 w-4', isHebrew ? "ml-2" : "mr-2")} />
          {translate("Add Price List")}
        </Button>
      </DialogTrigger>
      <DialogContent
        className={cn("lg:max-w-[850px] overflow-hidden pb-0", isHebrew ? "rtl" : "ltr")}
      >
        <DialogHeader>
          <DialogTitle>{translate("Add new price list")}</DialogTitle>
          <DialogDescription>{translate("Fill in the details to proceed")}.</DialogDescription>
        </DialogHeader>
        <div className='grid py-4 w-full'>
          <div className='grid gap-4'>
            <div className={cn(companyId && "hidden")}>
              <Label>
                {translate("Company")}
                <strong className='text-destructive'>*</strong>
              </Label>
              <Select
                onValueChange={(e) => {
                  const selectedCompany = workspaceContractors?.find(
                    (contractor: DictType) => contractor?.name === e,
                  );

                  setStateCompanyId(companyId ? companyId : selectedCompany?.id);
                }}
                disabled={!!companyId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={translate("Select Company")} />
                </SelectTrigger>

                <SelectContent>
                  {workspaceContractors?.map((company: any) => (
                    <SelectItem key={company?.id} value={company?.name}>
                      {company?.name}
                    </SelectItem>
                  ))}
                  <Separator />
                  <form onSubmit={onSubmitContractor} className='flex items-center gap-2 mt-1'>
                    <Input
                      placeholder={translate("Company name")}
                      className=''
                      required
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                    />
                    <Button size={"sm"} className='h-8 w-8 m-0 p-0'>
                      <PlusCircle className=' h-7 w-7' />
                    </Button>
                  </form>
                </SelectContent>
              </Select>
            </div>
          </div>

          <PriceListExcelMapper setMapping={setFileInformation} mappingType='price list' />

          <DialogFooter className=''>
            <Button
              type='button'
              variant='outline'
              onClick={() => {
                setShowUploadDialog(false);
                setFileInformation({});
              }}
            >
              {translate("Cancel")}
            </Button>
            <Button
              type='submit'
              disabled={!fileInformation?.File || !stateCompanyId}
              loading={addPriceListMutation.isPending}
              onClick={() => onSubmit()}
            >
              {addPriceListMutation.isPending ? translate("Uploading") : translate("Upload")}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddPriceList;
